import {
  useRestaurantStore,
  applyPrimaryColor,
  applyIconStyle,
  type Location,
  type ThemeMode,
  type IconBrand,
  type IconStyleName,
} from '@restaurant/core'
import { injectBrandTokens } from '../config/brand'
import { statusBarForeground } from './statusBar'

/**
 * Parent→showcase live customization (spec §2,
 * webeatery-device-frames docs/superpowers/specs/2026-07-29-live-customization-design.md).
 * The embedding preview page posts { webeateryCustomize: 1, appName?,
 * primaryColor?, themeBrightness?, themeStyle?, logoUrl?, iconBrand?,
 * iconStyle? }; we re-theme in place. Guards mirror markShowcaseState's outbound rules:
 * the embedder origin comes from document.referrer, and we only ever listen
 * to window.parent. The preview must never crash in front of a lead — every
 * apply path swallows its own errors.
 */

type CustomizeMessage = {
  webeateryCustomize: 1
  appName?: string
  primaryColor?: string
  themeBrightness?: string
  themeStyle?: string
  logoUrl?: string
  iconBrand?: string
  iconStyle?: string
}

export function isCustomizeMessage(data: unknown): data is CustomizeMessage {
  if (!data || typeof data !== 'object') return false
  const d = data as Record<string, unknown>
  if (d.webeateryCustomize !== 1) return false
  if (d.appName !== undefined && typeof d.appName !== 'string') return false
  if (d.primaryColor !== undefined && typeof d.primaryColor !== 'string') return false
  if (d.themeBrightness !== undefined && typeof d.themeBrightness !== 'string') return false
  if (d.themeStyle !== undefined && typeof d.themeStyle !== 'string') return false
  if (d.logoUrl !== undefined && typeof d.logoUrl !== 'string') return false
  if (d.iconBrand !== undefined && typeof d.iconBrand !== 'string') return false
  if (d.iconStyle !== undefined && typeof d.iconStyle !== 'string') return false
  return true
}

/**
 * Icon brand/style allowlists. Duplicated here rather than derived from core's
 * unions on purpose: this guard is the security boundary for anything the
 * embedder sends, so it must be an explicit value check, not a cast. Adding a
 * family to core's IconBrand means adding it here too — an omission degrades to
 * "keeps the previous family", never to an unvalidated lookup.
 */
const ICON_BRANDS: readonly string[] = ['phosphor']
const ICON_STYLES: readonly string[] = ['thin', 'regular', 'bold', 'thinDuo', 'regularDuo', 'boldDuo', 'fill']

/** Module-level showcase theme mode — read by ThemeContext's init when
 * data-showcase="1" (spec §3 unpin). Default matches the tenant force
 * (colorful/light) so an un-customized showcase still boots into the
 * expected look. */
let showcaseThemeMode: ThemeMode = 'colorfullight'
export function getShowcaseThemeMode(): ThemeMode { return showcaseThemeMode }
export function setShowcaseThemeMode(mode: ThemeMode): void { showcaseThemeMode = mode }

const LOGO_MAX_LENGTH = 1_500_000
function isValidLogoUrl(v: string): boolean {
  return v.length <= LOGO_MAX_LENGTH && /^data:image\/(png|jpeg|webp);base64,/.test(v)
}

/** Applies a logo data URL to the restaurant store. Restricted to inline
 * data: URIs (png/jpeg/webp) under a size cap — never an https URL, which
 * would let the embedder point the showcase at an arbitrary remote asset. */
function applyLogo(logoUrl: string): void {
  if (!isValidLogoUrl(logoUrl)) return
  try {
    const { currentRestaurant, setRestaurant } = useRestaurantStore.getState()
    if (currentRestaurant) setRestaurant({ ...currentRestaurant, logoUrl })
  } catch { /* never break the preview */ }
}

/** Live-verification finding (2026-07-29): the mobile menu header renders
 * `location.name`, not `restaurant.name` (LocationOrderType.web.tsx:39) — a
 * rename must also touch the seeded location(s), not just the restaurant,
 * or the visible header never updates even though the store did. */
function applyName(raw: string): void {
  const name = raw.trim()
  if (name.length < 1 || name.length > 40) return
  try {
    const { currentRestaurant, setRestaurant, locations, setLocations, currentLocation, setLocation } =
      useRestaurantStore.getState()
    if (currentRestaurant) setRestaurant({ ...currentRestaurant, name })
    if (locations.length) setLocations(locations.map((loc: Location) => ({ ...loc, name })))
    if (currentLocation) setLocation({ ...currentLocation, name })
    document.title = name
  } catch { /* never break the preview */ }
}

/** Final-review finding M1 (2026-07-29): injectShowcaseStatusBar paints the
 * simulated status-bar glyphs once at boot, forced-red-contrast (white) —
 * that element lives outside the React root and never repaints on retheme.
 * Reuses statusBarForeground (statusBar.ts) so the contrast rule has one
 * owner; a light color re-pick (e.g. #F4B400) must not leave low-contrast
 * white glyphs on a light bar. No-op if the bar isn't mounted (browser
 * showcase mode, or not yet injected). */
function updateStatusBarGlyphs(hex: string): void {
  const el = document.querySelector<HTMLElement>('[data-showcase-statusbar]')
  if (el) el.style.color = statusBarForeground(hex)
}

/** Live-verification finding (2026-07-29): components resolve brand colors
 * into JS style literals captured at first mount — there is no
 * subscription/re-render path, so a config-only update repaints the CSS
 * custom properties but not any already-mounted component. `remount`
 * (wired by bootShowcase, showcase mode only) forces a full app-tree
 * remount so everything re-resolves from the updated configs. Only fired
 * after a successful apply — never on rejected/invalid hex or on a
 * name-only message.
 *
 * Final-review finding I1 (2026-07-29): CustomizePanel always sends full
 * state (name AND color), so every debounced name keystroke re-sends the
 * unchanged color too — without a dedupe, that's a full remount per pause
 * while the lead is typing. `lastApplied` is closure state owned by THIS
 * installed listener (reset to null on every install — a fresh mount has
 * no prior color, so the first real apply always runs; the boot-forced
 * #FF0000 doesn't count as "applied" via this path, so a lead explicitly
 * re-picking #FF0000 later still applies). Returns the new lastApplied
 * value for the caller to store back — early-returns (no-op) the unchanged
 * value when the hex is invalid or identical to what's already applied.
 *
 * The remount decision now lives in the listener's consolidated tail (task
 * 3, spec §3): this function only applies the color/CSS vars/status-bar
 * glyphs and reports whether it changed via the returned value. */
function applyColor(hex: string, lastApplied: string | null): string | null {
  if (!/^#[0-9A-Fa-f]{6}$/.test(hex)) return lastApplied
  if (hex === lastApplied) return lastApplied
  try {
    applyPrimaryColor(hex)
    injectBrandTokens()
    updateStatusBarGlyphs(hex)
    return hex
  } catch {
    return lastApplied
  }
}

export type InstallCustomizeListenerOptions = {
  /** Forces a full app-tree remount after a successful primaryColor apply.
   * Omit outside showcase mode (or when no remount path exists) — the
   * listener still installs and applies, it just won't force a repaint. */
  remount?: () => void
}

/**
 * Install the listener. Returns an uninstall fn, or null when the embedder
 * origin cannot be established (empty/unparseable referrer — direct or
 * worker load): customization is then inert, the preview still works.
 * getReferrer is injectable for tests, same pattern as boot.ts getParent.
 */
export function installCustomizeListener(
  win: Window = window,
  getReferrer: () => string = () => document.referrer,
  { remount }: InstallCustomizeListenerOptions = {},
): (() => void) | null {
  const referrer = getReferrer()
  let embedderOrigin: string
  try {
    // new URL('') throws (Invalid URL), same as any unparseable referrer —
    // one guard covers both the empty (direct/worker load) and malformed cases.
    embedderOrigin = new URL(referrer).origin
  } catch {
    return null
  }
  // Owned by this installed listener — see applyColor's doc comment (finding I1).
  // brightness/style seed FROM the stored mode (not a hardcoded default) so a
  // reinstalled listener (e.g. after a remount) starts in sync with whatever
  // was already applied — otherwise a no-axis message would recompute a
  // mismatched candidate from the wrong defaults and spuriously remount.
  let lastAppliedHex: string | null = null
  let lastAppliedMode: ThemeMode = showcaseThemeMode
  let brightness: 'light' | 'dark' = showcaseThemeMode.endsWith('dark') ? 'dark' : 'light'
  let style: 'colorful' | 'muted' = showcaseThemeMode.startsWith('muted') ? 'muted' : 'colorful'
  // Icon brand + style are sticky axes for the same reason theme's are: both
  // halves resolve through one mapping function (core's resolveIconStyleId), so
  // a message may carry either alone and the other keeps its last value. Seeded
  // from core's own defaults (DEFAULT_ICON_STYLE_ID is phosFill), which is what
  // an un-customized preview boots into.
  let iconBrand: IconBrand = 'phosphor'
  let iconStyle: IconStyleName = 'fill'

  // Apply order: name → logo → theme → color → icons, then a single
  // consolidated tail remount iff theme or color actually changed (task 3,
  // spec §3). Icons come after color deliberately: applyPrimaryColor rebuilds
  // the whole brand config, and core carries the icon fields forward through
  // that rebuild — applying icons last means a single message carrying both a
  // new color and a new icon style lands the icon style whichever way that
  // carry-forward behaves.
  const onMessage = (event: MessageEvent) => {
    if (event.origin !== embedderOrigin) return
    if (event.source !== win.parent) return
    if (!isCustomizeMessage(event.data)) return
    const d = event.data
    if (d.appName !== undefined) applyName(d.appName)
    if (d.logoUrl !== undefined) applyLogo(d.logoUrl)

    let themeDirty = false
    if (d.themeBrightness === 'light' || d.themeBrightness === 'dark') brightness = d.themeBrightness
    if (d.themeStyle === 'colorful' || d.themeStyle === 'muted') style = d.themeStyle
    const candidate = `${style}${brightness}` as ThemeMode // colorfullight | colorfuldark | mutedlight | muteddark
    if (candidate !== lastAppliedMode) {
      try {
        setShowcaseThemeMode(candidate)
        lastAppliedMode = candidate
        themeDirty = true
      } catch { /* keep previous mode */ }
    }

    let colorDirty = false
    if (d.primaryColor !== undefined) {
      const prev = lastAppliedHex
      lastAppliedHex = applyColor(d.primaryColor, lastAppliedHex)
      colorDirty = lastAppliedHex !== prev
    }

    // Deliberately NOT part of the remount tail: every <Icon> subscribes to the
    // core icon store through useIconStyle (useSyncExternalStore), so mounted
    // icons re-render themselves — a remount would be a pointless full-tree
    // teardown. core's setIconStyle also early-returns on an unchanged id, so
    // the resend-full-state-per-keystroke pattern costs nothing here.
    if (d.iconBrand !== undefined || d.iconStyle !== undefined) {
      if (d.iconBrand !== undefined && ICON_BRANDS.includes(d.iconBrand)) iconBrand = d.iconBrand as IconBrand
      if (d.iconStyle !== undefined && ICON_STYLES.includes(d.iconStyle)) iconStyle = d.iconStyle as IconStyleName
      try {
        applyIconStyle(iconBrand, iconStyle)
      } catch { /* never break the preview */ }
    }

    if (themeDirty || colorDirty) remount?.()
  }
  win.addEventListener('message', onMessage)
  return () => win.removeEventListener('message', onMessage)
}
